import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

export interface SiteConfig {
  site_name: string;
  site_description: string;
  expanded_menu_logo_url: string;
  collapsed_menu_logo_url: string;
}

export interface AIConfig {
  provider: string;
  api_key: string;
  model_presets: {
    high: string;
    medium: string;
    low: string;
  };
}

export interface EmbeddingContextConfig {
  embedding_model: string;
  chunk_size: number;
  chunk_overlap: number;
  top_k_results: number;
}

export interface VectorStoreConfig {
  provider: string;
  base_url: string;
  access_token: string;
}

export interface FileStorageConfig {
  provider: string;
  bucket_name: string;
  access_key_id: string;
  secret_access_key: string;
  region: string;
  endpoint: string;
}

export interface GoogleOAuthConfig {
  client_id: string;
  client_secret: string;
  redirect_uri?: string;
}

export interface OnedriveOAuthConfig {
  client_id: string;
  client_secret: string;
  tenant_id?: string;
  object_id?: string;
}

export interface OnedriveRedirectUrisResponse {
  data: {
    redirect_uris: string[];
  };
}

export interface OnedriveRegisterRedirectUriResponse {
  data: {
    success: boolean;
    redirect_uri: string;
    message: string;
  };
}

export interface OnedriveUpdateRedirectUriResponse {
  data: {
    success: boolean;
    redirect_uri: string;
    message: string;
  };
}

export interface OnedriveDeleteRedirectUriResponse {
  data: {
    success: boolean;
    message: string;
  };
}

export interface DropboxOAuthConfig {
  client_id: string;
  client_secret: string;
}

export interface AppConfig {
  _id?: string;
  site: SiteConfig;
  ai_config: AIConfig;
  embedding_context_config: EmbeddingContextConfig;
  vector_store_config: VectorStoreConfig;
  file_storage_config: FileStorageConfig;
  google_oauth_config?: GoogleOAuthConfig;
  onedrive_oauth_config?: OnedriveOAuthConfig;
  dropbox_oauth_config?: DropboxOAuthConfig;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface SettingsResponse {
  success: boolean;
  code: number;
  data: AppConfig;
}

export interface FileUploadResponse {
  success: boolean;
  code: number;
  data: {
    name: string;
    key: string;
    url: string;
    fileType: string;
    fileSize: number;
    filePages: number;
  };
}

class SettingsService {
  async getSettings(): Promise<SettingsResponse> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SETTINGS.GET);
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  }

  async saveSettings(settingsData: AppConfig): Promise<SettingsResponse> {
    try {
      const method = settingsData._id ? 'put' : 'post';
      const response = await apiClient[method](API_ENDPOINTS.SETTINGS.SAVE, settingsData);
      return response.data;
    } catch (error) {
      console.error('Error saving settings:', error);
      throw error;
    }
  }

  async getOnedriveRedirectUris(): Promise<OnedriveRedirectUrisResponse> {
    const response = await apiClient.get(API_ENDPOINTS.SETTINGS.ONEDRIVE_REDIRECT_URIS);
    return response.data;
  }

  async registerOnedriveRedirectUri(redirect_uri: string): Promise<OnedriveRegisterRedirectUriResponse> {
    const response = await apiClient.post(API_ENDPOINTS.SETTINGS.ONEDRIVE_REGISTER_REDIRECT_URI, { redirect_uri });
    return response.data;
  }

  async updateOnedriveRedirectUri(old_redirect_uri: string, new_redirect_uri: string): Promise<OnedriveUpdateRedirectUriResponse> {
    const response = await apiClient.put(API_ENDPOINTS.SETTINGS.ONEDRIVE_UPDATE_REDIRECT_URI, { old_redirect_uri, new_redirect_uri });
    return response.data;
  }

  async deleteOnedriveRedirectUri(redirect_uri: string): Promise<OnedriveDeleteRedirectUriResponse> {
    const response = await apiClient.delete(API_ENDPOINTS.SETTINGS.ONEDRIVE_DELETE_REDIRECT_URI, { data: { redirect_uri } });
    return response.data;
  }

  async uploadFile(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.put<FileUploadResponse>(
        '/dms-v2-srv/storage-srv/utils/upload',
        formData
      );

      if (!response.data.success) {
        throw new Error('Upload was not successful');
      }

      return response.data.data.url;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  async uploadFileWithDetails(file: File): Promise<FileUploadResponse['data']> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.put<FileUploadResponse>(
        '/dms-v2-srv/storage-srv/utils/upload',
        formData
      );

      if (!response.data.success) {
        throw new Error('Upload was not successful');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }
}

export const settingsService = new SettingsService();
export default settingsService;